import {
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import DoneIcon from '@material-ui/icons/Done';
import RemoveIcon from '@material-ui/icons/RemoveCircle';
import WarningIcon from '@material-ui/icons/Warning';
import i18n from 'i18n';
import { getAllImagesLogsRequest } from 'modules/environments/actions';
import {
  EnvironmentsLogsImage,
  ListEnvironmentsLogsImagesFieldsDataProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { cyrb53Hash, formatDateWithMoment } from 'utils/helpers';
import Button from 'components/Button';
import { Query } from 'hooks/useTableFilters';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  id: string;
  imagensLogsList: ListEnvironmentsLogsImagesFieldsDataProps;
  instanceId: string;
  isLoadingLogs: boolean;
  query?: Query;
  getAllImagesLogsRequest: ({ id, query, instanceId }) => void;
  onCancel?: () => void;
}

const ImagesLogs = ({
  id,
  imagensLogsList,
  instanceId,
  isLoadingLogs,
  query,
  onCancel,
  getAllImagesLogsRequest,
}: Props) => {
  const classes = useStyles();
  React.useEffect(() => {
    getAllImagesLogsRequest({ id, query, instanceId });
  }, [getAllImagesLogsRequest, id, instanceId, query]);

  const status = {
    INFO: {
      icon: <DoneIcon fontSize="small" />,
      name: i18n.t('SUCCESS_KEY'),
    },
    ERROR: {
      icon: <RemoveIcon fontSize="small" />,
      name: 'Error',
    },
    WARNING: {
      icon: <WarningIcon fontSize="small" />,
      name: i18n.t('ATTENTION_KEY'),
    },
    NOTENOUGHDATA: {
      icon: <NotInterestedIcon fontSize="small" />,
      name: i18n.t('NOT_ENOUGH_DATA_KEY'),
    },
  };

  return (
    <Grid container>
      {!isLoadingLogs ? (
        <Grid>
          {imagensLogsList?.results?.length === 0 ? (
            <Grid>{i18n.t('CLIENTS_PAGE.NO_LOGS_KEY')}</Grid>
          ) : (
            <Grid className={classes.grid}>
              {imagensLogsList?.results?.map((item: EnvironmentsLogsImage) => (
                <Grid
                  className={classes.container}
                  key={cyrb53Hash(String(item?.timestamp))}
                >
                  <Grid>
                    {!item?.image_publication_start ? (
                      <Grid container>
                        <Typography
                          variant="caption"
                          className={
                            classes[item?.levelname.toLocaleLowerCase()]
                          }
                        >
                          {
                            status[
                              item?.levelname.replace(/\s/g, '').toUpperCase()
                            ].icon
                          }
                          {
                            status[
                              item?.levelname.replace(/\s/g, '').toUpperCase()
                            ].name
                          }
                        </Typography>
                        {item?.task_name && (
                          <Typography
                            variant="caption"
                            className={classes.work}
                          >
                            <strong className={classes.strong}>
                              {`${i18n.t('WORK_KEY')}:`}
                            </strong>{' '}
                            {i18n.t(
                              `ENVIRONMENTS_PAGE.${item?.task_name?.toLocaleUpperCase()}`
                            )}
                          </Typography>
                        )}
                      </Grid>
                    ) : (
                      <Typography variant="caption" className={classes.title}>
                        <strong>
                          {i18n.t(
                            'ENVIRONMENTS_PAGE.PUBLICATION_IMAGE_INIT_KEY'
                          )}
                        </strong>
                      </Typography>
                    )}

                    <Grid className={classes.operator}>
                      {Boolean(item?.new_version) && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>
                            {i18n.t('VERSION_KEY')}:
                          </strong>
                          {item?.new_version}
                        </Typography>
                      )}
                      {item?.operator && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>
                            {i18n.t('OPERATOR_KEY')}:{' '}
                          </strong>
                          {item?.operator} ({item?.operator_email})
                        </Typography>
                      )}
                      {item?.task_message && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>AMI: </strong>
                          {item?.task_message}
                        </Typography>
                      )}
                      {item?.publication_type && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>
                            {i18n.t('ENVIRONMENTS_PAGE.TYPE_PUBLICATION_KEY')}:{' '}
                          </strong>
                          {i18n.t(
                            `ENVIRONMENTS_PAGE.${item?.publication_type
                              .toString()
                              .replaceAll(' ', '_')
                              .toUpperCase()}_KEY`
                          )}
                        </Typography>
                      )}
                      {item?.instance_id && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>
                            {i18n.t('INSTANCE_KEY')}:{' '}
                          </strong>
                          {item?.instance_id}
                        </Typography>
                      )}
                      {item?.publication_identification && (
                        <Typography variant="caption">
                          <strong className={classes.textLight}>
                            {i18n.t(
                              'ENVIRONMENTS_PAGE.PUBLICATION_IDENTIFICATION'
                            )}
                            :{' '}
                          </strong>
                          {item?.publication_identification}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                  <Grid className={classes.dataContainer}>
                    <Typography variant="caption">
                      {formatDateWithMoment(item?.timestamp)}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
              {onCancel && (
                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={onCancel}
                  >
                    {i18n.t('CLOSE_KEY')}
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      ) : (
        <Grid container alignItems="center" justify="center">
          <CircularProgress size={20} />
        </Grid>
      )}
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { imagensLogsList, isLoadingLogs },
}: ApplicationState) => ({
  imagensLogsList,
  isLoadingLogs,
});

export default connect(mapStateToProps, {
  getAllImagesLogsRequest,
})(ImagesLogs);
